/**
 * The protocol-number refusal rule, for the browser.
 *
 * A MIRROR of functions/src/utils/protocolFields.ts (protocolNumberCandidate,
 * cleanProtocolField, isPlaceholderValue). The web app cannot import
 * functions code, so the rule is copied here and
 * functions/src/utils/protocolNumberRuleParity.test.ts fails if the two ever
 * answer differently. Change both together.
 *
 * WHY IT EXISTS (2026-10-01). ProtocolUploader.tsx auto-filled
 * studySetup.protocolNumber from the protocol's IND/IDE number. That was the
 * root cause of documents printing an IND number as the protocol number. It
 * now fills only from the protocol's own extracted protocol number, through
 * this rule.
 */

const PLACEHOLDER =
  /^(not\s+specified|unspecified|unknown|unknown\s*\/\s*not\s+yet\s+decided|not\s+yet\s+decided|tbd|tba)$/i;

const NOT_A_PROTOCOL_NUMBER =
  /^\s*(?:version\b|ver\.|v\.?\s*\d+(?:\.\d+)+|v\.?\s*\d+(?:\s|$)|amendment\b|draft\b|ind\s*(?:#|no\.?|:)?\s*:?\s*\d|ide\s*(?:#|no\.?|:)?\s*:?\s*g?\d|(?:ind|ide)(?:\s*\/\s*(?:ind|ide))?(?![-a-z0-9]))/i;

const BARE_VERSION = /^\s*\d+(?:\.\d+)+\s*(?:$|[,(;]|dated\b|\d{1,2}\s+[A-Za-z]{3,})/i;

const REGISTRY_ID =
  /^\s*(?:(?:EudraCT|EU[\s-]?CT|CTIS|ClinicalTrials\.gov|NCT|IND|IDE)\b\.?\s*(?:No\.?|Number|Identifier|#)?\s*:?\s*)?(?:NCT\s*\d{8}|\d{4}-\d{6}-\d{2}(?:-\d{2})?)(?:\s*\(\s*(?:EudraCT|EU[\s-]?CT|CTIS|ClinicalTrials\.gov)\s*\))?\s*[.,]?\s*$/i;

export function isPlaceholderValue(v: string): boolean {
  return PLACEHOLDER.test(v.trim());
}

export function cleanProtocolField(v: unknown): string {
  if (typeof v !== 'string') return '';
  const trimmed = v.trim();
  if (!trimmed) return '';
  if (isPlaceholderValue(trimmed)) return '';
  return trimmed;
}

/** A protocol-number candidate, or '' when it is not one. */
export function protocolNumberCandidate(v: unknown, notThese: Array<unknown> = []): string {
  const s = cleanProtocolField(v);
  if (!s) return '';
  if (NOT_A_PROTOCOL_NUMBER.test(s) || BARE_VERSION.test(s) || REGISTRY_ID.test(s)) return '';
  const fold = (x: unknown) => cleanProtocolField(x).toLowerCase();
  if (notThese.some((x) => fold(x) !== '' && fold(x) === s.toLowerCase())) return '';
  return s;
}

/** The protocol's own extracted protocol number, guarded; '' when none. */
export function autoFilledProtocolNumber(analysis: Record<string, unknown> | null | undefined): string {
  const a = analysis ?? {};
  return protocolNumberCandidate(a.protocolNumber, [a.protocolVersion, a.ideOrIndNumber, a.nctNumber]);
}
